import React, { useState } from 'react';
import './Deals.css';

const Deals = ({ products = [], loading, onRefresh, onAddOrder }) => {
    // Filter and Sort State
    const [selectedBrand, setSelectedBrand] = useState('All');
    const [selectedDealType, setSelectedDealType] = useState('All');
    const [sortBy, setSortBy] = useState('latest');
    const [mobileView, setMobileView] = useState('list'); // Default to list (1 column) for premium feel

    const handleRefresh = () => {
        if (onRefresh) onRefresh();
    };

    // Extract Unique Options
    const uniqueBrands = ['All', ...new Set(products.map(p => p.brandName || 'Other'))].sort();
    const uniqueDealTypes = ['All', ...new Set(products.map(p => p.dealType).filter(Boolean))].sort();
    // Filter Logic
    const filteredProducts = products.filter(product => {
        const matchBrand = selectedBrand === 'All' || (product.brandName || 'Other') === selectedBrand;
        const matchDealType = selectedDealType === 'All' || product.dealType === selectedDealType;
        return matchBrand && matchDealType;
    });

    // Sorting Logic - Responsive to user selection
    const sortedProducts = [...filteredProducts].sort((a, b) => {
        if (sortBy === 'latest') {
            const dateA = a.datePosted ? new Date(a.datePosted) : new Date(0);
            const dateB = b.datePosted ? new Date(b.datePosted) : new Date(0);
            const timeA = isNaN(dateA.getTime()) ? 0 : dateA.getTime();
            const timeB = isNaN(dateB.getTime()) ? 0 : dateB.getTime();
            return timeB - timeA || 0;
        }
        
        if (sortBy === 'price-low') {
            return (parseFloat(a.price) || 0) - (parseFloat(b.price) || 0);
        }
        
        if (sortBy === 'price-high') {
            return (parseFloat(b.price) || 0) - (parseFloat(a.price) || 0);
        }

        if (sortBy === 'less-low') {
            return (parseFloat(a.lessAmount) || 0) - (parseFloat(b.lessAmount) || 0);
        }

        if (sortBy === 'less-high') {
            return (parseFloat(b.lessAmount) || 0) - (parseFloat(a.lessAmount) || 0);
        }
        
        if (sortBy === 'brand') {
            return (a.brandName || '').localeCompare(b.brandName || '');
        }

        return 0;
    });

    // Grouping Logic (Default view) - Using sorted products
    const groupedProducts = sortedProducts.reduce((groups, product) => {
        const brand = product.brandName || 'Other';
        if (!groups[brand]) {
            groups[brand] = [];
        }
        groups[brand].push(product);
        return groups;
    }, {});

    // Sort Brands by choice (Recency, Alphabetical, Price, or Less)
    const sortedBrands = Object.keys(groupedProducts).sort((a, b) => {
        if (sortBy === 'brand') {
            return a.localeCompare(b);
        }

        const getBestValue = (brand, criteria) => {
            const values = groupedProducts[brand].map(p => {
                if (criteria.includes('price')) return parseFloat(p.price) || 0;
                if (criteria.includes('less')) return parseFloat(p.lessAmount) || 0;
                const d = new Date(p.datePosted);
                return isNaN(d.getTime()) ? 0 : d.getTime();
            });
            
            if (criteria === 'price-low' || criteria === 'less-low') {
                return Math.min(...values);
            }
            return Math.max(...values, 0);
        };

        if (sortBy === 'price-low' || sortBy === 'less-low') {
            return getBestValue(a, sortBy) - getBestValue(b, sortBy);
        }
        
        return getBestValue(b, sortBy) - getBestValue(a, sortBy);
    });

    return (
        <div className="deals-container">
            <button 
                onClick={handleRefresh} 
                className="deals-refresh-btn-corner" 
                disabled={loading}
                title="Refresh Deals"
            >
                <i className={`ri-refresh-line ${loading ? 'ri-spin' : ''}`}></i>
            </button>

            <div className="deals-header">
                <div className="header-content">
                    <h2><i className="ri-gift-line"></i> Available Deals</h2>
                    <p>Browse our latest products and exclusive offers</p>
                </div>
            </div>

            {/* Filter and Sort Controls */}
            <div className="deals-controls">
                <div className="controls-grid">
                    <div className="control-group">
                        <label>Brand:</label>
                        <select value={selectedBrand} onChange={(e) => setSelectedBrand(e.target.value)}>
                            {uniqueBrands.map(brand => (
                                <option key={brand} value={brand}>{brand}</option>
                            ))}
                        </select>
                    </div>

                    <div className="control-group">
                        <label>Deal Type:</label>
                        <select value={selectedDealType} onChange={(e) => setSelectedDealType(e.target.value)}>
                            {uniqueDealTypes.map(type => (
                                <option key={type} value={type}>{type}</option>
                            ))}
                        </select>
                    </div>

                    <div className="control-group sort-group">
                        <label>Sort By:</label>
                        <select value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
                            <option value="latest">Latest Deals</option>
                            <option value="price-low">Price: Low to High</option>
                            <option value="price-high">Price: High to Low</option>
                            <option value="less-low">Less: Low to High</option>
                            <option value="less-high">Less: High to Low</option>
                            <option value="brand">Brand: A to Z</option>
                        </select>
                    </div>

                    <div className="view-toggle-area">
                        <div className="view-toggle-group">
                            <button 
                                className={`view-toggle-btn ${mobileView === 'list' ? 'active' : ''}`}
                                onClick={() => setMobileView('list')}
                                title="List View"
                            >
                                <i className="ri-layout-row-line"></i>
                            </button>
                            <button 
                                className={`view-toggle-btn ${mobileView === 'grid' ? 'active' : ''}`}
                                onClick={() => setMobileView('grid')}
                                title="Grid View (2 Columns)"
                            >
                                <i className="ri-layout-grid-line"></i>
                            </button>
                        </div>
                    </div>
                </div>
                {onAddOrder && (
                    <div className="deals-action-row">
                        <button className="deals-add-order-btn gold-full-width" onClick={onAddOrder}>
                            <i className="ri-add-circle-fill"></i> Add New Order
                        </button>
                    </div>
                )}
            </div>


            {loading ? (
                <div className="loading-state">Loading products...</div>
            ) : filteredProducts.length === 0 ? (
                <div className="empty-state">
                    <div className="empty-icon"><i className="ri-shopping-bag-line"></i></div>
                    <p>No products match your filters</p>
                    <button className="btn-reset" onClick={() => { setSelectedBrand('All'); setSelectedDealType('All'); }}>
                        Reset Filters
                    </button>
                </div>
            ) : (
                <div className="brands-container">
                    {/* Grouped View (Default) */}
                    {sortedBrands.map((brand) => (
                        <div key={brand} className="brand-section">
                            <h3 className="brand-name">{brand}</h3>
                            <div className={`products-grid ${mobileView === 'grid' ? 'mobile-grid-2' : ''}`}>
                                {groupedProducts[brand].map((product, index) => (
                                    <ProductCard key={index} product={product} />
                                ))}
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

// Extracted Product Card for cleaner code
const ProductCard = ({ product }) => {
    // Function to convert various Google Drive link formats to direct image URLs
    const getDirectImageUrl = (url) => {
        if (!url) return '';
        
        // Handle common Google Drive link formats
        if (url.includes('drive.google.com') || url.includes('drive.usercontent.google.com')) {
            let fileId = '';
            
            // Format: drive.google.com/file/d/FILE_ID/view
            if (url.includes('/file/d/')) {
                fileId = url.split('/file/d/')[1].split('/')[0].split('?')[0];
            } 
            // Format: drive.google.com/open?id=FILE_ID or uc?id=FILE_ID or download?id=FILE_ID
            else if (url.includes('id=')) {
                const urlParams = new URLSearchParams(url.split('?')[1]);
                fileId = urlParams.get('id');
            }
            
            if (fileId) {
                return `https://drive.google.com/uc?export=view&id=${fileId}`;
            }
        }
        
        return url;
    };

    const imageUrl = getDirectImageUrl(product.productImage);

    // Helper to check if a deal is new (within 5 days)
    const isNew = (dateString) => {
        if (!dateString) return false;
        const dealDate = new Date(dateString);
        const today = new Date();
        const diffTime = today - dealDate;
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        return diffDays >= 0 && diffDays <= 5;
    };

    const showNewBadge = isNew(product.datePosted);

    return (
        <div className="product-card">
            {showNewBadge && (
                <div className="new-badge">NEW</div>
            )}
            {imageUrl ? (
                <div className="product-image-wrapper">
                    <img
                        src={imageUrl}
                        alt={product.productName}
                        className="product-image"
                        loading="lazy"
                        onError={(e) => {
                            e.target.onerror = null;
                            e.target.src = '/cwLogo.png'; // Fallback to logo
                        }}
                    />
                </div>
            ) : (
                <div className="product-image-placeholder">
                    <i className="ri-image-2-line"></i>
                </div>
            )}
            <div className="product-header">
                <h4>{product.productName}</h4>
                {product.price && (
                    <span className="product-price">₹{parseFloat(product.price).toFixed(2)}</span>
                )}
            </div>

            <div className="product-meta">
                {product.dealType && (
                    <span className="deal-type-badge">{product.dealType}</span>
                )}
                {parseFloat(product.lessAmount) > 0 ? (
                    <span className="less-amount-badge">
                        Less: ₹{parseFloat(product.lessAmount).toFixed(2)}
                    </span>
                ) : (
                    <span className="full-refund-badge">
                        Full Refund
                    </span>
                )}
            </div>

            {product.comment && (
                <div className="product-comment">
                    <i className="ri-information-line"></i> {product.comment}
                </div>
            )}

            <a
                href={product.productLink}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-order"
                style={{ width: '100%', textAlign: 'center', display: 'block', marginTop: 'auto' }}
            >
                <i className="ri-shopping-cart-line"></i> Order Now
            </a>
        </div>
    );
};

export default Deals;
